import React from 'react'
import { Provider } from 'react-redux'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { Switch, Route } from 'react-router-dom'
import PCLNAnalytics from '@pcln/analytics'
import { ThemeProvider } from 'pcln-design-system'
import { AceContentProvider } from '@pcln/ace-content-react'
import {
  Footer as PartnerFooter,
  Header as PartnerHeader,
  PartnerProvider,
} from '@pcln/whitelabel-components'
import { FeatureProvider } from '@pcln/feature-components'

import { pclnExpt as flyExpt } from '../common/fly-experimentation/fly-experimentation.js'
import { GlobalStyleOverride } from '../common/styles/globalStyleOverride'
import { ListingsContainer } from './containers/listings-container'
import { configureStore } from './store'
import { listingsApp } from './reducers'
import { enablePageReloadUponAuthentication } from './components/Legacy/fly-closed-user-group-deal/notification-authentication-checker'
import { clientLogger } from './util/clientLogger'
import { ErrorBoundary } from './components/ErrorBoundary'
import { translateSearchRequest } from './util/translateSearchRequest'
import { initializeSETI } from './util/transformSETI'
import { ContentfulProvider } from '@pcln/contentful-components'
import { fireFlyConditions } from './constants/fireflyCondition'
import { getTheme } from '../common/getTheme.js'

class App extends React.Component {
  constructor(props) {
    super(props)
    let preloadedState = this.props.store

    if (typeof window !== 'undefined') {
      // Store preloaded state
      preloadedState = window.__PRELOADED_STATE__

      // Initialize SETI singleton if not initialized earlier
      this.seti = preloadedState.seti
      initializeSETI(this.seti)

      // Refresh page on authentication action (login or logout)
      enablePageReloadUponAuthentication()
    }

    // Enable support for redux-devtools-extension in development
    let enableReduxDevTools = false

    // SETI: AIR_SEARCH_LOAD_ACE_CONTENT_COPY , Set Ace content
    this.aceContent = get(preloadedState, 'aceContent', {})

    if (
      typeof window !== 'undefined' &&
      get(preloadedState, 'pclnBootstrapData.requestLocals', null)
    ) {
      enableReduxDevTools =
        preloadedState.pclnBootstrapData.requestLocals.appInfo.appVersion ===
        'development'

      // [START] ACE Migration
      const PCLN_BOOTSTRAP_DATA = get(
        preloadedState,
        'pclnBootstrapData.requestLocals'
      )
      this.PCLN_BOOTSTRAP_DATA = PCLN_BOOTSTRAP_DATA

      if (get(PCLN_BOOTSTRAP_DATA, 'webstats.clientGUID', false)) {
        const experimentMetadata = {
          appCode: get(PCLN_BOOTSTRAP_DATA, 'webstats.isMobile', false)
            ? 'MOBILEWEB'
            : 'DESKTOP',
          globalPartnerCode: 'PCLN',
          productID: 1,
          productSubType: 'ALL',
          plfCode: 'PCLN',
          clientIP: PCLN_BOOTSTRAP_DATA.webstats.resolvedClientIP,
          serverTimestamp: PCLN_BOOTSTRAP_DATA.webstats.requestTime,
          referralID: get(PCLN_BOOTSTRAP_DATA, 'referral.id', ''),
          referralSourceID: get(PCLN_BOOTSTRAP_DATA, 'referral.sourceId', ''),
          referralClickID: get(PCLN_BOOTSTRAP_DATA, 'referral.clickId', ''),
          pageName: 'Flight Listings',
        }
        const config = {
          batchIntervalMS: 2000,
          batchSize: 20,
          useBatching: true,
          debugFireImpression: true,
          isIdleQueued: true,
        }

        const experimentDetails = Object.keys(PCLN_BOOTSTRAP_DATA.seti).map(
          data => {
            const {
              setiTagName,
              setiVariantName,
              resultMessage,
              resultCode,
            } = PCLN_BOOTSTRAP_DATA.seti[data]

            return {
              setiTagName,
              setiVariantName,
              resultMessage,
              resultCode,
            }
          }
        )

        flyExpt.init(
          experimentDetails,
          experimentMetadata,
          config,
          preloadedState.seti
        )
        flyExpt.fireEvent({
          eventType: 'pageViewEvent',
          pageName: 'Flight_react_listings_page',
        })

        // SET Firefly Conditions
        fireFlyConditions.setIsFireFly({
          pclnBootstrapData: preloadedState?.pclnBootstrapData || {},
          pclnExpt: flyExpt,
        })

        window.onbeforeunload = () => {
          flyExpt.clearBatch()
        }
      }
      // [END] ACE Migration

      this.appName = 'fly-search'
      this.appVersion = preloadedState.pclnBootstrapData.appVersion
      // Configure logging library
      PCLNAnalytics.appName = this.appName
      PCLNAnalytics.appVersion = this.appVersion

      // Configure logger used for sending search results
      clientLogger(preloadedState.pclnBootstrapData)

      const { tripType } = translateSearchRequest(
        preloadedState.pclnBootstrapData.searchRequest
      )

      PCLNAnalytics.logEvent({
        action: 'App load',
        category: 'MFLY-Listings',
        label: tripType,
      })

      PCLNAnalytics.logEvent(
        Object.assign(
          {
            event: 'server_timings',
            category: 'MFLY-Listings',
            label: tripType,
          },
          preloadedState.pclnBootstrapData.serverTimings
        )
      )

      this.logError = PCLNAnalytics.logError
    }
    this.state = {
      store: configureStore(listingsApp(), preloadedState, enableReduxDevTools),
    }

    // automation hook
    if (typeof window !== 'undefined') {
      window.autobotLifeCycle = {
        onAirSearchComplete: null,
        airSearchCompleted: false,
        airSearchRequestId: '',
      }
    }

    this.theme = getTheme(this?.PCLN_BOOTSTRAP_DATA?.partner?.partnerCode)
  }

  getChildContext() {
    return {
      airSearchPromise: this.props.airSearchPromise,
      pclnBootstrapData:
        this.PCLN_BOOTSTRAP_DATA || this.props.pclnBootstrapData,
    }
  }

  render() {
    const partner = this?.PCLN_BOOTSTRAP_DATA?.partner
    const features = this?.PCLN_BOOTSTRAP_DATA?.features
    const env = this?.PCLN_BOOTSTRAP_DATA?.pclnEnv
    const contentful = this?.PCLN_BOOTSTRAP_DATA?.contentful
    const partnerBaseImageUrl = '/shop/search'
    return (
      <ThemeProvider theme={this.theme}>
        <GlobalStyleOverride />
        <FeatureProvider features={features}>
          <ContentfulProvider contentful={contentful}>
            <PartnerProvider appName={this.appName} partner={partner} env={env}>
              <ErrorBoundary
                appName={this.appName}
                SETI={this.seti}
                appVersion={this.appVersion}
              >
                {partner && (
                  <PartnerHeader
                    partner={partner}
                    baseImageUrl={partnerBaseImageUrl}
                    isMobile={this.PCLN_BOOTSTRAP_DATA?.webstats?.isMobile}
                  />
                )}
                <AceContentProvider content={this.aceContent.FLIGHTSSEARCH}>
                  <Provider store={this.state.store}>
                    <Switch>
                      <Route path={'/:slices+'} component={ListingsContainer} />
                    </Switch>
                  </Provider>
                </AceContentProvider>
                {partner && <PartnerFooter partner={partner} />}
              </ErrorBoundary>
            </PartnerProvider>
          </ContentfulProvider>
        </FeatureProvider>
      </ThemeProvider>
    )
  }
}

App.childContextTypes = {
  airSearchPromise: PropTypes.object,
  pclnBootstrapData: PropTypes.object,
}

App.propTypes = {
  store: PropTypes.object,
  airSearchPromise: PropTypes.object,
  pclnBootstrapData: PropTypes.object,
  SETI: PropTypes.object,
}

export { App }
